import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import BottomNav from "../components/BottomNav";
import { Sparkles, Heart, X, Info, RotateCcw } from "lucide-react";

export default function Discover() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [profiles, setProfiles] = useState(null);
  const [matchModal, setMatchModal] = useState(null);
  const [history, setHistory] = useState([]);  // for rewind

  const load = async () => {
    try {
      const { data } = await api.get("/discover");
      setProfiles(data);
    } catch { setProfiles([]); }
  };

  useEffect(() => {
    if (!user?.questionnaire) { nav("/questionnaire"); return; }
    load();
  }, [user, nav]);

  const top = profiles?.[0];
  const next = profiles?.[1];

  const handleAction = async (action) => {
    if (!top) return;
    const current = top;
    setHistory((h) => [...h.slice(-4), current]);
    setProfiles((p) => p.slice(1));
    try {
      const { data } = await api.post("/like", { target_user_id: current.id, action });
      if (data.matched) setMatchModal(data);
    } catch { /* silent */ }
  };

  const rewind = () => {
    const last = history[history.length - 1];
    if (!last) return;
    setHistory((h) => h.slice(0, -1));
    setProfiles((p) => [last, ...p]);
  };

  return (
    <div className="app-shell bg-aurora min-h-screen pb-nav" data-testid="discover-page">
      <div className="px-6 pt-10 pb-3">
        <p className="text-xs uppercase tracking-widest text-[#082A24] font-medium">Ahoj {user?.name} 💕</p>
        <h1 className="font-display text-3xl text-[#332D38]">Objevuj</h1>
      </div>

      <div className="px-6">
        <div className="glass-card p-3 mb-4 flex items-center gap-3" data-testid="ai-banner">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#082A24] to-[#0F3D34] flex items-center justify-center shrink-0">
            <Sparkles size={14} className="text-white" />
          </div>
          <p className="text-xs text-[#332D38] leading-snug">Přejeď doprava = <strong className="text-[#0F3D34]">Líbí</strong>, doleva = <strong className="text-rose-500">Přeskoč</strong>. Nebo klepni na kartu pro detail.</p>
        </div>
      </div>

      {/* Card stack */}
      <div className="px-6 relative h-[540px] flex items-center justify-center">
        {profiles === null && <div className="h-full w-full rounded-3xl shimmer" />}
        {profiles?.length === 0 && (
          <div className="text-center text-[#8A7E91]" data-testid="no-profiles">
            <Heart className="mx-auto mb-3 text-[#8FA09C]" size={40} strokeWidth={1.5} />
            <p>Zatím žádné nové profily.</p>
            <p className="text-xs mt-1">Vrať se brzy ✨</p>
          </div>
        )}
        <AnimatePresence>
          {next && <BackCard key={`back-${next.id}`} profile={next} />}
          {top && <SwipeCard key={top.id} profile={top} onAction={handleAction} onOpen={() => nav(`/profile/${top.id}`)} />}
        </AnimatePresence>
      </div>

      {/* Bottom action bar */}
      {top && (
        <div className="px-6 mt-4">
          <div className="flex items-center justify-center gap-5" data-testid="swipe-actions">
            <button onClick={() => handleAction("skip")} className="w-16 h-16 rounded-full bg-white shadow-lg border border-slate-100 flex items-center justify-center active:scale-90 transition" data-testid="btn-skip">
              <X size={26} className="text-rose-500" strokeWidth={2.4} />
            </button>
            <button onClick={rewind} disabled={history.length === 0} className="w-12 h-12 rounded-full bg-white shadow-md border border-slate-100 flex items-center justify-center active:scale-90 transition disabled:opacity-30" data-testid="btn-rewind">
              <RotateCcw size={18} className="text-[#C9A66B]" strokeWidth={2.2} />
            </button>
            <button onClick={() => nav(`/profile/${top.id}`)} className="w-12 h-12 rounded-full bg-white shadow-md border border-slate-100 flex items-center justify-center active:scale-90 transition" data-testid="btn-info">
              <Info size={20} className="text-[#0F3D34]" strokeWidth={2.2} />
            </button>
            <button onClick={() => handleAction("like")} className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF7A00] to-[#E85D04] shadow-lg flex items-center justify-center active:scale-90 transition" data-testid="btn-like">
              <Heart size={26} fill="white" strokeWidth={0} className="text-white" />
            </button>
          </div>
        </div>
      )}

      {matchModal && <MatchModal data={matchModal} onClose={() => setMatchModal(null)} />}

      <BottomNav />
    </div>
  );
}

function SwipeCard({ profile, onAction, onOpen }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 0, 200], [-18, 0, 18]);
  const likeOpacity = useTransform(x, [30, 130], [0, 1]);
  const nopeOpacity = useTransform(x, [-130, -30], [1, 0]);

  const onDragEnd = (_, info) => {
    const threshold = 120;
    if (info.offset.x > threshold) onAction("like");
    else if (info.offset.x < -threshold) onAction("skip");
  };

  return (
    <motion.div
      className="absolute inset-0 rounded-3xl overflow-hidden bg-white shadow-[0_25px_60px_rgba(15,61,52,0.28)] cursor-grab active:cursor-grabbing"
      style={{ x, rotate }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.6}
      onDragEnd={onDragEnd}
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      data-testid={`swipe-card-${profile.id}`}
    >
      <div className="relative h-full">
        {profile.photo_url ? (
          <img src={profile.photo_url} alt={profile.name} className="w-full h-full object-cover pointer-events-none" draggable={false} />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#FFE4CC] to-[#FFD1A6] flex items-center justify-center text-8xl">💖</div>
        )}

        {/* LIKE / NOPE badges */}
        <motion.div style={{ opacity: likeOpacity }} className="absolute top-8 left-8 border-4 border-[#FF7A00] text-[#FF7A00] font-display text-3xl px-4 py-1.5 rounded-xl -rotate-12 bg-white/70">LÍBÍ</motion.div>
        <motion.div style={{ opacity: nopeOpacity }} className="absolute top-8 right-8 border-4 border-rose-500 text-rose-500 font-display text-3xl px-4 py-1.5 rounded-xl rotate-12 bg-white/70">NE</motion.div>

        {/* Compat badge */}
        <div className="absolute top-4 right-4 backdrop-blur-md bg-white/85 border border-white/60 rounded-full px-3 py-1.5 flex items-center gap-1.5 shadow-md">
          <Sparkles size={14} className="text-[#082A24]" />
          <span className="font-display font-semibold text-[#C9A66B]" data-testid={`compat-${profile.id}`}>{profile.compatibility}%</span>
        </div>

        {/* Info overlay */}
        <button onClick={onOpen} className="absolute bottom-0 left-0 right-0 p-6 text-left bg-gradient-to-t from-black/85 via-black/40 to-transparent text-white" data-testid={`open-detail-${profile.id}`}>
          <div className="flex items-end justify-between">
            <div>
              <h3 className="font-display text-3xl">{profile.name}, {profile.age}</h3>
              {profile.bio && <p className="text-sm opacity-90 line-clamp-2 mt-1">{profile.bio}</p>}
            </div>
            <div className="bg-white/20 backdrop-blur rounded-full px-3 py-1.5 text-xs flex items-center gap-1"><Info size={12} /> Detail</div>
          </div>
        </button>
      </div>
    </motion.div>
  );
}

function BackCard({ profile }) {
  return (
    <div className="absolute inset-0 rounded-3xl overflow-hidden bg-white shadow-[0_10px_30px_rgba(0,0,0,0.1)] scale-[0.94] opacity-70">
      {profile.photo_url ? (
        <img src={profile.photo_url} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#FFE4CC] to-[#FFD1A6] flex items-center justify-center text-6xl">💖</div>
      )}
    </div>
  );
}

function MatchModal({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/50 backdrop-blur-sm" data-testid="match-modal">
      <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
        <div className="heart-pulse text-6xl mb-3">💖</div>
        <h2 className="font-display text-3xl text-[#332D38] mb-2">Je to match!</h2>
        <p className="text-[#54635F] mb-6">Ty a <strong>{data.with_user?.name}</strong> jste se líbili navzájem. Napiš první!</p>
        <Link to={`/chat/${data.match_id}`}>
          <button className="btn-primary mb-3" data-testid="match-go-chat">Otevřít chat ✨</button>
        </Link>
        <button onClick={onClose} className="text-sm text-[#8A7E91]" data-testid="match-close">Pokračovat v objevování</button>
      </div>
    </div>
  );
}
